import { useRef, useEffect } from 'react'
import type { AddLayerObject, FilterSpecification, GeoJSONSource } from 'maplibre-gl'
import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson'
import { GEOJSON_LAYER_PREFIX } from './constants'
import { useMap } from './mapContext'

/** The layer id a `GeoJsonFillLayer`/`GeoJsonLineLayer` with this `id` puts on
 *  the map. The source id is the bare `id` — only the layer carries the
 *  prefix, which is how `MapView`'s raster anchoring tells vector layers
 *  apart from the raster layers it manages itself. */
export function geoJsonLayerId(id: string): string {
  return `${GEOJSON_LAYER_PREFIX}${id}`
}

// The real GeoJSON FeatureCollection type, imported explicitly from the
// `geojson` module rather than named off the ambient `GeoJSON` global
// namespace — a hand-rolled `{ type: string; coordinates: unknown }` shape
// here once "type-checked" only by accident: with this package's tsconfig
// `types` list restricted to `["vitest/globals", "vite/client"]`, `@types/geojson`
// was never loaded, so maplibre-gl's own `GeoJSON.GeoJSON`-typed parameters
// silently resolved to `any` under `skipLibCheck`. A consumer whose tsconfig
// doesn't restrict `types` (e.g. `apps/wehunt-web-prototype`, which transitively
// type-checks this file) loads the real ambient types and correctly rejected
// the loose shape (`type: string` isn't assignable to a literal geometry tag).
// The explicit module import sidesteps ambient-namespace visibility entirely.
type GeoJsonFeatureCollection = FeatureCollection<Geometry, GeoJsonProperties>

// `paint`/`layout` are deliberately loose (`Record<string, unknown>`) on the
// public props, so the property-name unions maplibre's real setter signatures
// require are cast away through this narrower view of the same two methods.
interface LooseStyleSetters {
  setPaintProperty(layer: string, name: string, value: unknown): unknown
  setLayoutProperty(layer: string, name: string, value: unknown): unknown
}

interface GeoJsonLayerProps {
  id: string
  data: GeoJsonFeatureCollection
  paint: Record<string, unknown>
  layout?: Record<string, unknown>
  filter?: unknown
  /** Full layer id (already prefixed) to insert this layer below. */
  beforeId?: string
}

export type GeoJsonLineLayerProps = GeoJsonLayerProps
export type GeoJsonFillLayerProps = GeoJsonLayerProps
export type GeoJsonHeatmapLayerProps = GeoJsonLayerProps

/** Declarative source+layer lifecycle shared by the fill and line variants:
 *  add once on mount, reconcile `data`/`paint`/`layout`/`filter` on change,
 *  remove (layer, then source) on unmount. Each reconciliation effect diffs
 *  against the previous render's value, seeded from the very prop the mount
 *  effect already applied via `addSource`/`addLayer` — so the first render
 *  never re-issues a redundant setter call, only a later prop change does. */
function useGeoJsonLayer(type: 'fill' | 'line' | 'heatmap', props: GeoJsonLayerProps): null {
  const { id, data, paint, layout, filter, beforeId } = props
  const map = useMap()
  const layerId = geoJsonLayerId(id)

  const initialRef = useRef({ data, paint, layout, filter, beforeId })
  initialRef.current = { data, paint, layout, filter, beforeId }

  useEffect(() => {
    if (!map) return
    const init = initialRef.current
    map.addSource(id, { type: 'geojson', data: init.data })
    map.addLayer(
      {
        id: layerId,
        type,
        source: id,
        paint: init.paint,
        // MapLibre's real style validation rejects a `layout`/`filter` KEY
        // that is present with value `undefined` ("object expected, undefined
        // found" / "array expected, undefined found") — it does not treat that
        // the same as the key being absent. A caller that omits `layout`/
        // `filter` (most `GeoJsonFillLayer`/`GeoJsonLineLayer` usages do) must
        // therefore not have the key on the object at all, not merely `undefined`
        // on it. The `@wehunt/map/testing` fake every unit test renders against
        // never validates the layer spec, so this was invisible until this
        // package's e2e suite exercised the real engine.
        ...(init.layout !== undefined ? { layout: init.layout } : {}),
        ...(init.filter !== undefined ? { filter: init.filter } : {}),
      } as AddLayerObject,
      init.beforeId,
    )
    return () => {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
      if (map.getSource(id)) map.removeSource(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, id, layerId, type])

  const prevData = useRef(data)
  useEffect(() => {
    if (map && data !== prevData.current) {
      map.getSource<GeoJSONSource>(id)?.setData(data)
    }
    prevData.current = data
  }, [map, id, data])

  const prevPaint = useRef(paint)
  useEffect(() => {
    if (map) {
      // Union of both key sets: a key present only in the PREVIOUS paint
      // (dropped by this render) must still be visited, or its stale value
      // is left on the layer forever — `undefined` tells maplibre to reset
      // that property back to the style-spec default.
      const keys = new Set([...Object.keys(prevPaint.current), ...Object.keys(paint)])
      for (const key of keys) {
        if (paint[key] !== prevPaint.current[key]) {
          ;(map as unknown as LooseStyleSetters).setPaintProperty(layerId, key, paint[key])
        }
      }
    }
    prevPaint.current = paint
  }, [map, layerId, paint])

  const prevLayout = useRef(layout)
  useEffect(() => {
    if (map) {
      const current = layout ?? {}
      const previous = prevLayout.current ?? {}
      const keys = new Set([...Object.keys(previous), ...Object.keys(current)])
      for (const key of keys) {
        if (current[key] !== previous[key]) {
          ;(map as unknown as LooseStyleSetters).setLayoutProperty(layerId, key, current[key])
        }
      }
    }
    prevLayout.current = layout
  }, [map, layerId, layout])

  const prevFilter = useRef(filter)
  useEffect(() => {
    if (map && filter !== prevFilter.current) {
      map.setFilter(layerId, filter as FilterSpecification | null | undefined)
    }
    prevFilter.current = filter
  }, [map, layerId, filter])

  return null
}

export function GeoJsonFillLayer(props: GeoJsonFillLayerProps): null {
  return useGeoJsonLayer('fill', props)
}

export function GeoJsonLineLayer(props: GeoJsonLineLayerProps): null {
  return useGeoJsonLayer('line', props)
}

export function GeoJsonHeatmapLayer(props: GeoJsonHeatmapLayerProps): null {
  return useGeoJsonLayer('heatmap', props)
}
